import React, { useEffect, useState, useCallback } from "react";
import { Play, Star, Calendar, ChevronLeft, ChevronRight, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

interface Movie {
  backdrop_path: string | null;
  vote_average: number | null;
  release_date: string | null;
  first_air_date?: string | null;
  title?: string;
  name?: string;
  overview: string | null;
  id: number;
  trailers: { key: string }[];
  media_type?: "movie" | "tv";
}

const FADE_VARIANTS = {
  enter: { opacity: 0, y: 24 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
};

const BG_VARIANTS = {
  enter: { opacity: 0, scale: 1.06 },
  center: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.97 },
};

const Hero = () => {
  const [currentMovie, setCurrentMovie] = useState(0);
  const [trendingMovies, setTrendingMovies] = useState<Movie[]>([]);
  const [direction, setDirection] = useState(1);

  const API_KEY = "859afbb4b98e3b467da9c99ac390e950";
  const TRENDING_MOVIES_URL = `https://api.themoviedb.org/3/trending/movie/week?api_key=${API_KEY}`;
  const POPULAR_TV_URL = `https://api.themoviedb.org/3/tv/popular?api_key=${API_KEY}`;

  useEffect(() => {
    const fetchTrendingContent = async () => {
      try {
        const [moviesResponse, tvResponse] = await Promise.all([
          axios.get(TRENDING_MOVIES_URL),
          axios.get(POPULAR_TV_URL),
        ]);
        const movies = moviesResponse.data.results.slice(0, 5);
        const tvShows = tvResponse.data.results.slice(0, 5);

        const [moviesWithTrailers, tvWithTrailers] = await Promise.all([
          Promise.all(
            movies.map(async (movie: Movie) => {
              const res = await axios.get(
                `https://api.themoviedb.org/3/movie/${movie.id}/videos?api_key=${API_KEY}`
              );
              return { ...movie, trailers: res.data.results, media_type: "movie" as const };
            })
          ),
          Promise.all(
            tvShows.map(async (tv: Movie) => {
              const res = await axios.get(
                `https://api.themoviedb.org/3/tv/${tv.id}/videos?api_key=${API_KEY}`
              );
              return { ...tv, trailers: res.data.results, media_type: "tv" as const };
            })
          ),
        ]);

        const allContent: Movie[] = [];
        const maxLength = Math.max(tvWithTrailers.length, moviesWithTrailers.length);
        for (let i = 0; i < maxLength; i++) {
          if (i < tvWithTrailers.length) allContent.push(tvWithTrailers[i]);
          if (i < moviesWithTrailers.length) allContent.push(moviesWithTrailers[i]);
        }

        setTrendingMovies(allContent);
      } catch (error) {
        console.error("Failed to fetch trending content", error);
      }
    };

    fetchTrendingContent();
  }, []);

  useEffect(() => {
    if (trendingMovies.length === 0) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentMovie((prev) => (prev + 1) % trendingMovies.length);
    }, 8000);
    return () => clearInterval(timer);
  }, [trendingMovies.length]);

  const prevMovie = useCallback(() => {
    setDirection(-1);
    setCurrentMovie((prev) => (prev === 0 ? trendingMovies.length - 1 : prev - 1));
  }, [trendingMovies.length]);

  const nextMovie = useCallback(() => {
    setDirection(1);
    setCurrentMovie((prev) => (prev + 1) % trendingMovies.length);
  }, [trendingMovies.length]);

  const goToMovie = useCallback(
    (index: number) => {
      setDirection(index > currentMovie ? 1 : -1);
      setCurrentMovie(index);
    },
    [currentMovie]
  );

  const movie: Movie | undefined = trendingMovies[currentMovie];
  const backdropUrl = movie?.backdrop_path
    ? `https://image.tmdb.org/t/p/original${movie.backdrop_path}`
    : "";

  return (
    <div className="relative h-[72vh] min-h-[360px] sm:h-[92vh] sm:min-h-[520px] flex flex-col overflow-hidden bg-black">
      <AnimatePresence mode="sync">
        <motion.div
          key={`bg-${currentMovie}`}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${backdropUrl}')` }}
          variants={BG_VARIANTS}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 1.2, ease: "easeInOut" }}
        />
      </AnimatePresence>

      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

      <div
        className="absolute bottom-0 left-0 w-96 h-48 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at bottom left, rgba(220,38,38,0.15) 0%, transparent 60%)",
        }}
      />

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-red-600/40 via-red-500/20 to-transparent" />

      <div className="relative flex-grow flex items-center px-4 sm:px-8 lg:px-16 z-10 pb-16 md:pb-0">
        <AnimatePresence mode="wait">
          <motion.div
            key={`content-${currentMovie}`}
            className="max-w-2xl ml-2 sm:ml-6 lg:ml-12"
            variants={FADE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.55, ease: "easeOut" }}
          >
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="hero-glass-badge flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
                <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                <span className="text-white font-semibold text-xs sm:text-sm">
                  {movie?.vote_average?.toFixed(1)} Rating
                </span>
              </div>

              <div className="hero-glass-badge flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
                <Calendar className="w-3.5 h-3.5 text-green-400" />
                <span className="text-zinc-200 text-xs sm:text-sm">
                  {movie?.release_date || movie?.first_air_date}
                </span>
              </div>

              <div
                className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-bold shadow-lg backdrop-blur-md ${
                  movie?.media_type === "tv"
                    ? "bg-blue-600/60 text-white border border-blue-400/30"
                    : "bg-red-600/60 text-white border border-red-400/30"
                }`}
                style={{
                  boxShadow:
                    movie?.media_type === "tv"
                      ? "0 0 12px rgba(37,99,235,0.3)"
                      : "0 0 12px rgba(220,38,38,0.3)",
                }}
              >
                {movie?.media_type === "tv" ? "TV Series" : "Movie"}
              </div>
            </div>

            <h1 className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 sm:mb-5 text-white leading-tight tracking-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              {movie?.title || movie?.name}
            </h1>

            <p className="text-white/90 text-sm sm:text-base md:text-lg mb-7 sm:mb-10 line-clamp-3 max-w-xl leading-relaxed drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] font-medium">
              {movie?.overview}
            </p>

            <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-3 sm:gap-4">
              <button
                onClick={() =>
                  window.open(
                    `https://www.youtube.com/watch?v=${movie?.trailers?.[0]?.key}`,
                    "_blank"
                  )
                }
                className="hero-btn-glow group relative flex items-center justify-center gap-2.5 px-7 sm:px-8 py-3 sm:py-3.5 rounded-full font-bold text-white text-sm sm:text-base overflow-hidden bg-red-600 hover:bg-red-500 transition-colors"
                aria-label="Watch Trailer"
              >
                <span className="hero-btn-shine" aria-hidden="true" />
                <Play className="w-4 h-4 sm:w-5 sm:h-5 fill-white relative z-10 group-hover:scale-110 transition-transform duration-200" />
                <span className="relative z-10">Watch Trailer</span>
              </button>

              <Link
                to={
                  movie?.media_type === "tv"
                    ? `/tv/${movie?.id}`
                    : `/movie/${movie?.id}`
                }
                className="hero-btn-glass group flex items-center justify-center gap-2.5 px-7 sm:px-8 py-3 sm:py-3.5 rounded-full font-bold text-white text-sm sm:text-base no-underline bg-black/40 backdrop-blur-md border border-white/20 hover:bg-black/60 hover:border-white/40 transition-all"
                aria-label="More Info"
              >
                <Info className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 group-hover:text-red-400 transition-colors duration-200" />
                <span>More Info</span>
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {trendingMovies.length > 1 && (
        <div className="flex md:hidden absolute bottom-4 left-1/2 -translate-x-1/2 z-20 items-center gap-2 bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
          <button
            onClick={prevMovie}
            className="p-1 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white transition-all"
            aria-label="Previous Slide"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-1.5 px-1">
            {trendingMovies.map((_, index) => (
              <button
                key={index}
                onClick={() => goToMovie(index)}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={currentMovie === index}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  currentMovie === index
                    ? "w-4 bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]"
                    : "w-1.5 bg-white/40"
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextMovie}
            className="p-1 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 text-white transition-all"
            aria-label="Next Slide"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {trendingMovies.length > 1 && (
        <div className="hidden md:flex absolute bottom-6 right-8 lg:right-16 z-20 items-center gap-2">
          <button
            onClick={prevMovie}
            className="p-3 rounded-2xl bg-black/40 hover:bg-black/60 active:scale-95 backdrop-blur-xl border border-white/20 transition-all duration-200 text-white shadow-xl hover:border-white/40"
            aria-label="Previous Slide"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextMovie}
            className="p-3 rounded-2xl bg-black/40 hover:bg-black/60 active:scale-95 backdrop-blur-xl border border-white/20 transition-all duration-200 text-white shadow-xl hover:border-white/40"
            aria-label="Next Slide"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {trendingMovies.length > 1 && (
        <div className="hidden md:flex absolute bottom-6 left-8 lg:left-16 z-20 items-center gap-2.5 px-3.5 py-2.5 rounded-2xl bg-black/40 backdrop-blur-xl border border-white/20 shadow-xl">
          {trendingMovies.map((_, index) => (
            <button
              key={index}
              onClick={() => goToMovie(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={currentMovie === index}
              className={`h-2 rounded-full transition-all duration-500 ${
                currentMovie === index
                  ? "w-8 bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.8)]"
                  : "w-2 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Hero;